import React, { FC, memo, ReactNode } from 'react';
import { Modal, Button } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import * as styles from './CommandDialog.module.css';

interface CommandDialogProps {
  visible: boolean;
  onClose: () => void;
  type: 'success' | 'warning' | 'error';
  title: string;
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
}

const CommandDialog: FC<CommandDialogProps> = ({
  visible,
  onClose,
  type,
  title,
  description,
  confirmText = 'Done',
  cancelText,
  onConfirm,
}) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleFilled style={{ fontSize: '64px', color: '#4CAF50' }} />;
      case 'warning':
        return <ExclamationCircleFilled style={{ fontSize: '64px', color: '#FAAD14' }} />;
      case 'error':
        return <CloseCircleFilled style={{ fontSize: '64px', color: '#FF4D4F' }} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={null} // Usually command dialogs might not have a close X if there is a button, but let's see. User said "seperate dialog similar like to waitlist". Waitlist has close icon. Use logic from Waitlist for container.
      // But typically success dialogs encourage clicking the main button.
      // Let's stick to simple modal for now, reusing some classes if possible or inline styles for simplicity as I don't want to mess up shared CSS too much without need.
      // User said "similar like to waitlist". WaitlistModal uses `waitlistModalContainer`.
      className={styles.commandDialogContainer}
      styles={{ body: { padding: '40px 32px 32px', boxShadow: 'none', textAlign: 'center' } }}
      width={'26.563rem'}
      centered
      rootClassName="command-dialog"
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
        {getIcon()}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <div className={styles.modalSubtitle}>{description}</div>
        </div>

        <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'center' }}>
          {cancelText && (
            <Button onClick={onClose} className={styles.cancelButtonCustom}>
              {cancelText}
            </Button>
          )}
          <Button type="primary" onClick={onConfirm || onClose} className={styles.doneButtonCustom}>
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

CommandDialog.displayName = 'CommandDialog';
export default memo(CommandDialog);
